import React, { useState, useEffect } from "react";
import {
  Alert,
  Autocomplete,
  Button,
  MenuItem,
  Snackbar,
  TextField,
} from "@mui/material";
import axios from "axios";
import { Spinner } from "../Spinner/Spinner";

// This component will handle the Plan Details popup logic and UI.
const PlanDetailsPopup = ({
  open,
  // onClose,
  onApplyAction,
  groupedItemsToActivate,
  accountDetails,
  actionTargetField,
  actionId,
  isWhitelistIpChecked,
  isWhitelistNumberChecked,
  tempWhitelistIpGroup,
  tempWhitelistNumberGroup,
  getDataRequestIdByName,
  getSmsRequestIdByName,
  activateSimsWithWhitelisting,
  setIsWhitelistIpChecked,
  setIsWhitelistNumberChecked,
  setTempWhitelistIpGroup,
  setTempWhitelistNumberGroup,
  setShowPlanDetailsPopup,
  dataWhitelistOptions,
  smsWhitelistOptions,
  loadingDataWhitelistOptions,
  loadingSmsWhitelistOptions,
  setGroupedItemsToActivate,
  setLoadingDataWhitelistOptions,
  iskycChecked,
  setIsKycChecked,
  kycForm,
  setKycForm,
}) => {
  const [selectedPlans, setSelectedPlans] = useState({});
  const [kycErrors, setKycErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const companyId = localStorage.getItem("userId");
  const [planError, setPlanError] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState("Airtel");

  const profileOptions = [
    { label: "Airtel", value: "Airtel" },
    { label: "BSNL", value: "BSNL" },
    { label: "Vodafone", value: "Vodafone" },
  ];

  const dataWhiteListOption = selectedProfile === "Airtel" ? dataWhitelistOptions : [];
  const smsWhiteListOption = selectedProfile === "Airtel" ? smsWhitelistOptions : [];
  // Add custom scrollbar styles for webkit browsers
  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      .plan-scroll-container::-webkit-scrollbar {
        height: 8px;
      }
      .plan-scroll-container::-webkit-scrollbar-track {
        background: #f0f0f0;
        border-radius: 10px;
      }
      .plan-scroll-container::-webkit-scrollbar-thumb {
        background: #4151e0;
        border-radius: 10px;
      }
      .plan-scroll-container::-webkit-scrollbar-thumb:hover {
        background: #3a47d1;
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  useEffect(() => {
    setSelectedPlans({});
    setKycErrors({});
  }, [groupedItemsToActivate]);

  const handlePlanToggle = (account, planType) => {
    setSelectedPlans((prev) => {
      const current = prev[account];
      return {
        ...prev,
        [account]: current === planType ? null : planType,
      };
    });
  };

  const DEVICE_TYPE_OPTIONS = [
    "VEHICLE_TRACKING",
    "SMART_METER",
    "ASSET_TRACKING",
    "POS_MACHINE",
    "HOME_AUTOMATION",
    "INDUSTRIAL_AUTOMATION",
    "SURVEILLANCE",
    "PERSONAL_TRACKER",
    "TRAFFIC_MANAGEMENT",
    "OTHERS",
  ];

  const handleChange = (field, value) => {
    setKycForm((prev) => ({ ...prev, [field]: value }));

    // Clear errors for the field being changed
    if (kycErrors[field]) {
      setKycErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateKycForm = () => {
    if (!iskycChecked) return true;

    const errors = {};

    // Validate Device IMEI
    const imei = String(kycForm.deviceImei || "").trim();
    if (!imei) {
      errors.deviceImei = "Device IMEI is required.";
    } else if (imei.length < 12 || imei.length > 16) {
      errors.deviceImei = "IMEI must be between 12 to 16 digits long.";
    } else if (!/^\d+$/.test(imei)) {
      errors.deviceImei = "IMEI must contain only digits.";
    }

    // Validate Device Type
    if (!kycForm.deviceType) {
      errors.deviceType = "Device type is required.";
    }

    // Validate Custom Device Type if OTHERS is selected
    if (
      kycForm.deviceType === "OTHERS" &&
      !String(kycForm.devicetypeother || "").trim()
    ) {
      errors.devicetypeother =
        "Custom device type is required when 'OTHERS' is selected.";
    }

    // Validate Device Model
    if (!String(kycForm.deviceModel || "").trim()) {
      errors.deviceModel = "Device model is required.";
    }

    // Validate Custodian Name
    if (!String(kycForm.custodianName || "").trim()) {
      errors.custodianName = "Custodian name is required.";
    }

    // Validate Registration Number
    if (!String(kycForm.registrationNo || "").trim()) {
      errors.registrationNo = "Registration number is required.";
    }

    // Validate Custodian Address
    if (!String(kycForm.custodianAddress || "").trim()) {
      errors.custodianAddress = "Custodian address is required.";
    }

    setKycErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const processSimActivationDirectly = async (allItemsToUpdate) => {
    let isSuccess = false;

    try {
      setLoadingDataWhitelistOptions(true);
      const activation = true;

      const dataWhitelisting = false;
      const smsWhitelisting = isWhitelistNumberChecked ? true : false;

      let operationType = "ACTIVATE";
      if (isWhitelistIpChecked && isWhitelistNumberChecked && iskycChecked) {
        operationType = "ACTIVATE+DATA+SMS+KYC";
      } else if (
        isWhitelistIpChecked &&
        !isWhitelistNumberChecked &&
        iskycChecked
      ) {
        operationType = "ACTIVATE+DATA+KYC";
      } else if (
        !isWhitelistIpChecked &&
        isWhitelistNumberChecked &&
        iskycChecked
      ) {
        operationType = "ACTIVATE+SMS+KYC";
      } else if (
        !isWhitelistIpChecked &&
        !isWhitelistNumberChecked &&
        iskycChecked
      ) {
        operationType = "ACTIVATE+KYC";
      } else if (isWhitelistIpChecked && isWhitelistNumberChecked) {
        operationType = "ACTIVATE+DATA+SMS";
      } else if (isWhitelistIpChecked && !isWhitelistNumberChecked) {
        operationType = "ACTIVATE+DATA";
      } else if (!isWhitelistIpChecked && isWhitelistNumberChecked) {
        operationType = "ACTIVATE+SMS";
      }

      const simUpdatePayload = allItemsToUpdate.map((item) => {
        const payload = {
          msisdn: item.msisdn,
          profile: item.vendor,
          simState: item.simState,
          planId: item.planId,
          validUpto: item.validity,
          operation: operationType,
          iccid: item.iccid,
          euiccId: item.euiccid,
          dataWhitelisting,
          smsWhitelisting,
          activation,
          selectedProfile,
        };

        if (iskycChecked) {
          payload.deviceImei = String(kycForm.deviceImei);
          payload.deviceType = kycForm.deviceType;
          payload.deviceModel = kycForm.deviceModel;
          payload.custodianName = kycForm.custodianName;
          payload.registrationNo = kycForm.registrationNo;
          payload.custodianAddress = kycForm.custodianAddress;
          if (kycForm.deviceType === "OTHERS") {
            payload.deviceTypeOthers = kycForm.devicetypeother;
          }
        }

        if (isWhitelistIpChecked && tempWhitelistIpGroup) {
          const dataRequestId = getDataRequestIdByName(tempWhitelistIpGroup);
          if (dataRequestId) payload.dataWhiteListRequestId = dataRequestId;
        }

        if (isWhitelistNumberChecked && tempWhitelistNumberGroup) {
          const smsRequestId = getSmsRequestIdByName(tempWhitelistNumberGroup);
          if (smsRequestId) payload.smsWhiteListRequestId = smsRequestId;
        }

        return payload;
      });

      console.log("Direct Activation Payload:", simUpdatePayload);

      const res = await activateSimsWithWhitelisting(simUpdatePayload);
      console.log("API response:", res);

      isSuccess = true;

      let message = "Your Request has been processed successfully.";
      if (Array.isArray(res) && Array.isArray(res[0])) {
        const messages = res[0].map((item) => {
          const mainMsg = item.message || "";
          const statusMsg = item.airtelResponse?.data?.statusMessage || "";
          return `${mainMsg} ${statusMsg}`;
        });
        message = messages.join("\n");
      }

      alert(message);
    } catch (error) {
      console.error("API update failed", error);
      alert("Something went wrong while updating SIM state.");
    } finally {
      setLoadingDataWhitelistOptions(false);

      if (isSuccess) {
        onApplyAction(allItemsToUpdate);
        setShowPlanDetailsPopup(false);
        setGroupedItemsToActivate({});
        setSelectedPlans({});
        setIsWhitelistIpChecked(false);
        setIsWhitelistNumberChecked(false);
        setTempWhitelistIpGroup("");
        setTempWhitelistNumberGroup("");
        setKycErrors({});
      }
    }
  };

  const handleClosePlanDetailsPopup = async () => {
    let allItemsToUpdate = [];

    for (const accountName in groupedItemsToActivate) {
      const itemsInAccount = groupedItemsToActivate[accountName];
      const selectedPlanForAccount = selectedPlans[accountName];

      if (selectedPlanForAccount) {
        const selectedPlanDetails =
          accountDetails[accountName]?.[selectedPlanForAccount] || {};

        allItemsToUpdate = allItemsToUpdate.concat(
          itemsInAccount.map((item) => ({
            ...item,
            [actionTargetField]: actionId,
            simState: "Active",
            selectedPlan: selectedPlanForAccount,
            validity: selectedPlanDetails.Valid_Upto || "",
            planData: selectedPlanDetails.data || "",
            planSms: selectedPlanDetails.sms || "",
            planCost: selectedPlanDetails.cost || "",
            planId: selectedPlanDetails.id,
            selectedProfile,
          })),
        );
      } else {
        allItemsToUpdate = allItemsToUpdate.concat(itemsInAccount);
      }
    }

    // 🔴 DO NOT CLOSE POPUP IF NO ITEMS
    if (allItemsToUpdate.length === 0) {
      return;
    }

    // 🔴 PLAN VALIDATION (Snackbar trigger)
    const hasMissingPlan = allItemsToUpdate.some(
      (item) => !item.planId || item.planId === "",
    );

    if (hasMissingPlan) {
      setPlanError(true); // Snackbar
      return; // 👈 STOP here, popup stays open
    }

    // 🔴 KYC VALIDATION
    if (iskycChecked && !validateKycForm()) {
      return; // keep popup open
    }

    // Start loading
    setLoading(true);

    try {
      await processSimActivationDirectly(allItemsToUpdate);
    } catch (error) {
      console.error("SIM activation failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const grandTotal = Object.entries(groupedItemsToActivate).reduce(
    (acc, [accountName, items]) => {
      const selectedPlan = selectedPlans[accountName];
      const planCost = accountDetails[accountName]?.[selectedPlan]?.cost;
      const costValue = parseInt(planCost?.replace("₹", "")) || 0;
      return acc + costValue * items.length;
    },
    0,
  );

  const simData = Object.entries(groupedItemsToActivate).map(
    ([accountName, item]) => {
      return item.map((sim) => {
        return sim._tertiaryData;
      });
    },
  );

  const allHaveTertiary = simData.flat().every((item) => item !== null);

  return (
    <>
      <Snackbar
        open={planError}
        autoHideDuration={3000}
        onClose={() => setPlanError(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{
          zIndex: 11000, // 🔥 higher than popup (10000)
        }}
      >
        <Alert severity="error" onClose={() => setPlanError(false)}>
          Please select plan before proceeding.
        </Alert>
      </Snackbar>

      {loading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 10001, // Higher than popup's 10000
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(0, 0, 0, 0.7)",
          }}
        >
          <Spinner />
        </div>
      )}
      {/* Backdrop */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          zIndex: 9999,
        }}
        onClick={() => {
          setShowPlanDetailsPopup(false);
          setIsWhitelistIpChecked(false);
          setIsWhitelistNumberChecked(false);
          setTempWhitelistIpGroup("");
          setTempWhitelistNumberGroup("");
          setKycErrors({});
        }}
      />

      {/* Popup Content */}
      <div
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          background: "white",
          padding: "0",
          borderRadius: "12px",
          boxShadow: "0 4px 15px rgba(0,0,0,0.3)",
          zIndex: 10000,
          width: "90vw",
          maxWidth: "1200px",
          minHeight: "50vh",
          maxHeight: "90vh",
          overflowY: "auto",
          textAlign: "center",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            backgroundColor: "#4151e0",
            color: "white",
            padding: "1rem 2rem",
            borderTopLeftRadius: "12px",
            borderTopRightRadius: "12px",
            fontWeight: "bold",
            fontSize: "1.25rem",
            textAlign: "center",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          Plan Details
          <span>
            <button
              style={{
                marginBottom: "1.5rem",
                padding: "0.8rem 2rem",
                borderRadius: "8px",
                border: "none",
                background: "rgb(12, 29, 180)",
                color: "white",
                fontWeight: "bold",
                fontSize: "1rem",
                cursor: "pointer",
                transition: "all 0.3s ease",
              }}
              onClick={() => {
                setShowPlanDetailsPopup(false);
                setIsWhitelistIpChecked(false);
                setIsWhitelistNumberChecked(false);
                setTempWhitelistIpGroup("");
                setTempWhitelistNumberGroup("");
                setKycErrors({});
              }}
            >
              close
            </button>
          </span>
        </div>

        <div style={{ padding: "2rem" }}>
          <div style={{ marginBottom: "2rem", textAlign: "left" }}>
            <div style={{ display: "flex", gap: "0", marginBottom: "1rem" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "0.75rem 1.5rem",
                  backgroundColor: "#4CAF50",
                  color: "white",
                  borderRadius: "8px 0 0 8px",
                  cursor: "default",
                  fontWeight: "bold",
                  fontSize: "0.9rem",
                  border: "1px solid #ddd",
                  borderRight: "none",
                  minWidth: "120px",
                  justifyContent: "center",
                  opacity: 1,
                }}
              >
                <span style={{ marginRight: "0.5rem" }}>✓</span>
                Activation
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "0.75rem 1.5rem",
                  backgroundColor: isWhitelistIpChecked ? "#4CAF50" : "#f5f5f5",
                  color: isWhitelistIpChecked ? "white" : "#333",
                  cursor: "pointer",
                  fontWeight: "bold",
                  fontSize: "0.9rem",
                  transition: "all 0.3s ease",
                  border: "1px solid #ddd",
                  borderRight: "none",
                  minWidth: "120px",
                  justifyContent: "center",
                }}
                onClick={() => {
                  const newValue = !isWhitelistIpChecked;
                  setIsWhitelistIpChecked(newValue);
                  if (!newValue) {
                    setTempWhitelistIpGroup("");
                  }
                }}
              >
                {isWhitelistIpChecked && (
                  <span style={{ marginRight: "0.5rem" }}>✓</span>
                )}
                Data Whitelist
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "0.75rem 1.5rem",
                  backgroundColor: isWhitelistNumberChecked
                    ? "#4CAF50"
                    : "#f5f5f5",
                  color: isWhitelistNumberChecked ? "white" : "#333",
                  cursor: "pointer",
                  fontWeight: "bold",
                  fontSize: "0.9rem",
                  transition: "all 0.3s ease",
                  border: "1px solid #ddd",
                  minWidth: "140px",
                  justifyContent: "center",
                }}
                onClick={() => {
                  const newValue = !isWhitelistNumberChecked;
                  setIsWhitelistNumberChecked(newValue);
                  if (!newValue) {
                    setTempWhitelistNumberGroup("");
                  }
                }}
              >
                {isWhitelistNumberChecked && (
                  <span style={{ marginRight: "0.5rem" }}>✓</span>
                )}
                SMS Whitelist
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                  marginLeft: "30px",
                }}
              >
                <div style={{ display: "flex", gap: "10px" }}>
                  {profileOptions.map((option) => (
                    <Button
                      key={option.value}
                      variant={
                        selectedProfile === option.value
                          ? "contained"
                          : "outlined"
                      }
                      onClick={() => {
                        setSelectedProfile(option.value);
                        setSelectedPlans({});
                      }}
                      sx={{
                        minWidth: "100px",
                        textTransform: "none",
                        borderRadius: "8px",
                      }}
                    >
                      {option.label}
                    </Button>
                  ))}
                </div>
              </div>
            </div>

            {(isWhitelistIpChecked ||
              isWhitelistNumberChecked ||
              iskycChecked) && (
              <div style={{ marginTop: "1rem" }}>
                {(isWhitelistIpChecked || isWhitelistNumberChecked) && (
                  <div
                    style={{
                      display: "flex",
                      gap: "1rem",
                      marginBottom: "1rem",
                    }}
                  >
                    {isWhitelistIpChecked && (
                      <div style={{ flex: 1 }}>
                        <label
                          style={{
                            display: "block",
                            marginBottom: "0.5rem",
                            fontWeight: "bold",
                            color: "#333",
                          }}
                        >
                          Data Whitelist Group:
                        </label>
                        <Autocomplete
                          fullWidth
                          options={dataWhiteListOption || []}
                          getOptionLabel={(option) => option || ""}
                          filterSelectedOptions
                          disablePortal
                          loading={loadingDataWhitelistOptions}
                          value={tempWhitelistIpGroup}
                          onChange={(event, newValue) => {
                            setTempWhitelistIpGroup(newValue || "");
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              size="small"
                              placeholder={"Select Data Whitelist group"}
                              fullWidth
                            />
                          )}
                        />
                      </div>
                    )}

                    {isWhitelistNumberChecked && (
                      <div style={{ flex: 1 }}>
                        <label
                          style={{
                            display: "block",
                            marginBottom: "0.5rem",
                            fontWeight: "bold",
                            color: "#333",
                          }}
                        >
                          SMS Whitelist Group:
                        </label>
                        <Autocomplete
                          fullWidth
                          options={smsWhiteListOption || []}
                          getOptionLabel={(option) => option || ""}
                          filterSelectedOptions
                          disablePortal
                          loading={loadingSmsWhitelistOptions}
                          value={tempWhitelistNumberGroup}
                          onChange={(event, newValue) => {
                            setTempWhitelistNumberGroup(newValue || "");
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              size="small"
                              placeholder={"Select SMS Whitelist group"}
                              fullWidth
                            />
                          )}
                        />
                      </div>
                    )}
                  </div>
                )}

                {iskycChecked && (
                  <div style={{ marginTop: "1.5rem", textAlign: "left" }}>
                    <h4 style={{ marginBottom: "1rem", color: "#333" }}>
                      KYC Details
                    </h4>
                    <div
                      style={{
                        display: "grid",
                        gap: "1rem",
                        gridTemplateColumns: "1fr 1fr",
                      }}
                    >
                      {Object.keys(kycForm)
                        .filter((key) => key !== "devicetypeother")
                        .map((key) => {
                          let label;

                          if (key === "registrationNo") {
                            label = "Registration NO. (eg Vehicle Reg NO.)";
                          } else if (key === "deviceImei") {
                            label = "Device IMEI (12-16 Digits)*";
                          } else {
                            label =
                              key
                                .replace(/([A-Z])/g, " $1")
                                .replace(/^./, (str) => str.toUpperCase()) +
                              "*";
                          }

                          if (key === "deviceType") {
                            const isOther = kycForm[key] === "OTHERS";

                            return (
                              <div
                                key={key}
                                style={{
                                  marginBottom: "1.5rem",
                                  gridColumn: isOther ? "1 / -1" : "auto",
                                }}
                              >
                                <TextField
                                  select
                                  fullWidth
                                  variant="outlined"
                                  label={label}
                                  value={kycForm[key] || ""}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    handleChange(key, val);
                                    if (val !== "OTHERS") {
                                      handleChange("devicetypeother", "");
                                    }
                                  }}
                                  SelectProps={{ native: true }}
                                  error={!!kycErrors[key]}
                                  helperText={kycErrors[key]}
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: "10px",
                                    },
                                  }}
                                >
                                  <option value="">Select Device Type</option>
                                  {DEVICE_TYPE_OPTIONS.map((option) => (
                                    <option key={option} value={option}>
                                      {option.replace(/_/g, " ")}
                                    </option>
                                  ))}
                                </TextField>

                                {isOther && (
                                  <TextField
                                    fullWidth
                                    variant="outlined"
                                    label="Custom Device Type*"
                                    value={kycForm.devicetypeother || ""}
                                    onChange={(e) =>
                                      handleChange(
                                        "devicetypeother",
                                        e.target.value,
                                      )
                                    }
                                    error={!!kycErrors.devicetypeother}
                                    helperText={kycErrors.devicetypeother}
                                    sx={{
                                      marginTop: "0.5rem",
                                      "& .MuiOutlinedInput-root": {
                                        borderRadius: "10px",
                                      },
                                    }}
                                  />
                                )}
                              </div>
                            );
                          }

                          return (
                            <TextField
                              key={key}
                              fullWidth
                              variant="outlined"
                              label={label}
                              value={kycForm[key] || ""}
                              disabled={key === "msisdn"}
                              onChange={(e) => {
                                const value = e.target.value;
                                handleChange(key, value);
                              }}
                              error={!!kycErrors[key]}
                              helperText={kycErrors[key]}
                              sx={{
                                marginBottom: "1rem",
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "10px",
                                },
                              }}
                            />
                          );
                        })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {Object.entries(groupedItemsToActivate).map(
            ([accountName, itemsInAccount]) => {
              const allplans = accountDetails[accountName] || [];

              let plans;
              if (selectedProfile === "Airtel") {
                plans = allplans?.filter((plan) => plan?.network === "Airtel");
              } else if (selectedProfile === "Vodafone") {
                plans = allplans?.filter((plan) => plan?.network === "Vodafone");
              } else if (selectedProfile === "BSNL") {
                plans = allplans?.filter(
                  (plan) => plan?.network === "BSNL",
                );
              } else {
                plans = {};
              }
              const selectedPlan = selectedPlans[accountName];

              // Filter to get only Activation plans
              const activationPlanKeys = Object.keys(plans).filter(
                (planKey) => plans[planKey]?.planType === "Activation",
              );

              // Skip this account if no Activation plans available
              // if (activationPlanKeys.length === 0) return null;

              const renderPlan = (planType) => {
                const plan = plans[planType];

                // Filter to show only Activation plans
                if (!plan || plan.planType !== "Activation") return null;

                const isSelected = selectedPlans[accountName] === planType;

                return (
                  <div
                    key={planType}
                    style={{
                      border: isSelected
                        ? "2px solid #4151e0"
                        : "1px dashed #aaa",
                      padding: "0.75rem",
                      borderRadius: "8px",
                      marginRight: "1rem",
                      backgroundColor: isSelected ? "#e7ebff" : "#f9f9f9",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      transform: isSelected ? "scale(1.02)" : "scale(1)",
                      boxShadow: isSelected
                        ? "0 2px 8px rgba(65, 81, 224, 0.3)"
                        : "none",
                      minWidth: "220px",
                      flexShrink: 0,
                    }}
                    onClick={() => handlePlanToggle(accountName, planType)}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: "0.5rem",
                      }}
                    >
                      <input
                        type="radio"
                        name={accountName}
                        checked={isSelected}
                        readOnly
                        style={{
                          marginRight: "0.5rem",
                          transform: "scale(1.3)",
                          accentColor: "#4151e0",
                          cursor: "pointer",
                        }}
                      />
                      <strong
                        style={{
                          color: isSelected ? "#4151e0" : "#333",
                          fontSize: "1rem",
                          transition: "color 0.3s ease",
                        }}
                      >
                        {plan.planName}
                      </strong>
                      {isSelected && (
                        <span
                          style={{
                            marginLeft: "auto",
                            color: "#4CAF50",
                            fontSize: "0.9rem",
                            fontWeight: "bold",
                            animation: "fadeIn 0.3s ease",
                          }}
                        >
                          ✓ Selected
                        </span>
                      )}
                    </div>

                    <div
                      style={{
                        marginLeft: "1.5rem",
                        marginTop: "0.5rem",
                        textAlign: "left",
                        opacity: isSelected ? 1 : 0.8,
                        transition: "opacity 0.3s ease",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: "0.3rem",
                        }}
                      >
                        <strong style={{ color: "#555" }}>DATA:</strong>
                        <span style={{ color: "#333", fontWeight: "500" }}>
                          {plan.data}
                        </span>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: "0.3rem",
                        }}
                      >
                        <strong style={{ color: "#555" }}>SMS:</strong>
                        <span style={{ color: "#333", fontWeight: "500" }}>
                          {plan.sms}
                        </span>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: "0.3rem",
                        }}
                      >
                        <strong style={{ color: "#555" }}>Validity:</strong>
                        <span style={{ color: "#333", fontWeight: "500" }}>
                          {plan.Valid_Upto}
                        </span>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: "0.3rem",
                        }}
                      >
                        <strong style={{ color: "#555" }}>Plan Type:</strong>
                        <span style={{ color: "#333", fontWeight: "500" }}>
                          {plan.planType}
                        </span>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <strong style={{ color: "#555" }}>COST:</strong>
                        <span
                          style={{
                            color: "#4151e0",
                            fontWeight: "bold",
                            fontSize: "1.1rem",
                          }}
                        >
                          {plan.cost}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              };

              return (
                <div
                  key={accountName}
                  style={{
                    paddingBottom: "1.5rem",
                    borderBottom: "1px solid #ddd",
                    marginBottom: "1.5rem",
                    textAlign: "left",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "1rem",
                    }}
                  >
                    {/* Left: Account + Items */}
                    <div>
                      <div
                        style={{
                          display: "flex",
                          gap: "0.5rem",
                          marginBottom: "0.4rem",
                          alignItems: "center",
                        }}
                      >
                        <strong style={{ color: "#333" }}>Account:</strong>
                        <span style={{ color: "#4151e0", fontWeight: "600" }}>
                          {accountName || "N/A"}
                        </span>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          gap: "0.5rem",
                          alignItems: "center",
                        }}
                      >
                        <strong style={{ color: "#333" }}>
                          Items to Activate:
                        </strong>
                        <span style={{ color: "#666" }}>
                          {itemsInAccount.length}
                        </span>
                      </div>
                    </div>

                    {/* Right: Carrier Profile dropdown */}
                  </div>

                  <div style={{ marginBottom: "1rem" }}>
                    <h4
                      style={{
                        color: "#333",
                        marginBottom: "0.8rem",
                        fontSize: "1.1rem",
                        fontWeight: "600",
                      }}
                    >
                      Available Plans:
                    </h4>
                    <div
                      className="plan-scroll-container"
                      style={{
                        display: "flex",
                        gap: "1rem",
                        overflowX: "auto",
                        paddingBottom: "1rem",
                        scrollbarWidth: "thin",
                        scrollbarColor: "#4151e0 #f0f0f0",
                        maxWidth: "100%",
                      }}
                    >
                      {activationPlanKeys.length > 0 ? (
                        Object.keys(plans).map((planName) =>
                          renderPlan(planName),
                        )
                      ) : (
                        <div
                          style={{
                            padding: "1rem",
                            color: "#666",
                            background: "#fafafa",
                            borderRadius: "8px",
                            border: "1px dashed #eee",
                          }}
                        >
                          No activation plans available for the selected
                          profile.
                        </div>
                      )}
                    </div>
                  </div>

                  {selectedPlans[accountName] && (
                    <button
                      style={{
                        marginTop: "0.5rem",
                        fontSize: "0.85rem",
                        background: "linear-gradient(45deg, #ff6b6b, #ee5a5a)",
                        border: "none",
                        color: "white",
                        cursor: "pointer",
                        padding: "0.4rem 0.8rem",
                        borderRadius: "6px",
                        fontWeight: "500",
                        transition: "all 0.3s ease",
                        boxShadow: "0 2px 4px rgba(238, 90, 90, 0.3)",
                      }}
                      onClick={() =>
                        setSelectedPlans((prev) => ({
                          ...prev,
                          [accountName]: null,
                        }))
                      }
                    >
                      Clear Selection
                    </button>
                  )}
                </div>
              );
            },
          )}
        </div>
        <div
          style={{
            padding: "1rem",
            textAlign: "center",
            fontWeight: "bold",
            fontSize: "1.5rem",
          }}
        >
          Total Payable Amount: ₹{grandTotal}
        </div>
        <button
          style={{
            marginBottom: "2.5rem",
            padding: "0.8rem 2rem",
            borderRadius: "8px",
            border: "none",
            background: "linear-gradient(45deg, #4151e0, #3a47d1)",
            color: "white",
            fontWeight: "bold",
            fontSize: "1rem",
            cursor: "pointer",
            transition: "all 0.3s ease",
            boxShadow: "0 4px 8px rgba(65, 81, 224, 0.3)",
          }}
          onClick={handleClosePlanDetailsPopup}
        >
          Proceed to Payment
        </button>
      </div>
    </>
  );
};

export default PlanDetailsPopup;
